import { useCallback, useEffect, useMemo, useState } from "react";
import {
  InstallerOffersPageService,
  type InstallerOfferJobRow,
  type InstallerOfferRow,
  type InstallerOffersPageQuery,
  type InstallerOffersSummary,
  type PageResponse,
} from "@/services/installer-offers-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useInstallerOffersPage(query: InstallerOffersPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<InstallerOfferJobRow | InstallerOfferRow>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void InstallerOffersPageService.getPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.page, query.pageSize, query.refreshToken, query.search, query.tab]);

  return { ...state, loading };
}

export function useInstallerOffersSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<InstallerOffersSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void InstallerOffersPageService.getSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useInstallerOffersServerData(query: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  tab: InstallerOffersPageQuery["tab"];
}) {
  const [refreshToken, setRefreshToken] = useState(0);
  const page = useInstallerOffersPage({
    enabled: query.enabled,
    page: query.page ?? 0,
    pageSize: query.pageSize ?? 20,
    search: query.search,
    tab: query.tab,
    refreshToken,
  });
  const summary = useInstallerOffersSummary({ enabled: query.enabled, refreshToken });

  const rows = useMemo(() => page.data, [page.data]);
  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  return {
    rows,
    count: page.count,
    error: page.error ?? summary.error,
    loading: page.loading || summary.loading,
    summary: summary.data,
    refresh,
  };
}
